import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { getEmployeeBySoeid } from "../../../../../../apis";
import { ModalContainer } from "../../components/ModalContainer";
import { InvestigationBlockInfo } from "../../components/InvestigationBlockInfo";
import { InvestigationButton } from "../../components/InvestigationButton";
import { InvolvedSectionTitle } from "../InvolvedSectionTitle";
import { getSeniority, isEmployeeActive } from "../soeidRecord";
import Icon from "../../../../../Icon/Icon";
import dayjs from "dayjs";

// Valida contra el servicio de RH el SOEID capturado en el involucrado y, si el
// usuario confirma, guarda el resultado en involved.soeid
export const ValidateSoeid = ({
  show,
  onHide,
  involved,
  onConfirm,
  isLoading = false,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // El SOEID vive en el campo `additional` (SOEID / GEID / numero de nomina)
  const soeid = involved?.additional?.trim() ?? "";

  // idle | loading | done | empty | error
  const [status, setStatus] = useState("idle");
  // Un SOEID puede traer varios registros (reingresos, o la persona aparece en
  // mas de una empresa). Llegan con el vigente primero, que es el que se
  // preselecciona; el investigador puede cambiarlo antes de confirmar.
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(0);
  const [loadedAt, setLoadedAt] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const employee = records[selected] ?? null;

  const search = useCallback(async () => {
    setStatus("loading");
    setRecords([]);
    setSelected(0);
    try {
      const { records: found, loadedAt: loaded } = await getEmployeeBySoeid(soeid);
      setRecords(found);
      setLoadedAt(loaded);
      // Sin registros no es un error: ese SOEID no esta en la nomina cargada.
      setStatus(found.length ? "done" : "empty");
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  }, [soeid]);

  useEffect(() => {
    if (!show) {
      setStatus("idle");
      setRecords([]);
      setSelected(0);
      setShowRaw(false);
      return;
    }
    if (soeid) search();
  }, [show, soeid, search]);

  const handleConfirm = async () => {
    if (!employee) return;
    await onConfirm({
      ...employee,
      validated_at: new Date().toISOString(),
    });
  };

  // Una fecha de renuncia manda sobre la clave de estatus
  const statusLabel = employee
    ? isEmployeeActive(employee)
      ? t("involved.soeid_status_active")
      : employee.resignation_date
      ? t("involved.soeid_status_inactive")
      : employee.status ?? ""
    : "";

  const formatDate = (date) =>
    date ? dayjs(date).format("DD/MMM/YYYY").toLocaleUpperCase() : "";

  // La antiguedad se calcula al vuelo: lo que se guarda es la fecha de ingreso
  const seniority = getSeniority(
    employee?.hire_date,
    employee?.resignation_date ? new Date(employee.resignation_date) : undefined
  );
  const seniorityLabel = seniority
    ? [
        seniority.years > 0 &&
          t("involved.soeid_seniority_years", { count: seniority.years }),
        seniority.months > 0 &&
          t("involved.soeid_seniority_months", { count: seniority.months }),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  // El bloque de ejecutivo solo trae datos si la persona atiende clientes
  const hasExecutiveData = Boolean(
    employee?.executive_status ||
      employee?.executive_office_code ||
      employee?.executive_start_date ||
      employee?.executive_updated_at
  );

  const half = { xs: 12, sm: 6 };

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Typography
        fontWeight={700}
        fontSize={28}
        color={theme.palette.ethicsBlue.main}
      >
        {t("involved.validate_soeid")}
      </Typography>
      <Typography fontSize={16} color="oxfordBlue">
        {t("involved.soeid_modal_description")}
      </Typography>
      <div className="hr-div"></div>

      {/* A quien se le esta validando */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 3 }}
        marginBottom={2}
      >
        <Box>
          <Typography fontSize={12} fontWeight={600} color="secondary">
            {t("involved.soeid_involved")}
          </Typography>
          <Typography fontSize={16}>{involved?.name}</Typography>
        </Box>
        {soeid && (
          <Box>
            <Typography fontSize={12} fontWeight={600} color="secondary">
              {t("involved.soeid_queried")}
            </Typography>
            <Typography fontSize={16} fontWeight={600}>
              {soeid}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Sin SOEID capturado: aqui ira el buscador cuando exista */}
      {!soeid && (
        <Stack alignItems="center" spacing={1} paddingY={3}>
          <Icon
            name="outline_user_search"
            size={40}
            color={theme.palette.blueGray200.main}
          />
          <Typography fontSize={16} fontWeight={600} color="oxfordBlue">
            {t("involved.soeid_missing")}
          </Typography>
          <Typography fontSize={14} color="secondary" textAlign="center">
            {t("involved.soeid_missing_hint")}
          </Typography>
        </Stack>
      )}

      {soeid && status === "loading" && (
        <Stack alignItems="center" spacing={1.5} paddingY={4}>
          <CircularProgress size={32} />
          <Typography fontSize={14} color="secondary">
            {t("involved.soeid_searching")}
          </Typography>
        </Stack>
      )}

      {/* El SOEID no esta en la nomina: no es un fallo del servicio */}
      {soeid && status === "empty" && (
        <Stack alignItems="center" spacing={1.5} paddingY={3}>
          <Icon
            name="outline_user_search"
            size={36}
            color={theme.palette.blueGray200.main}
          />
          <Typography fontSize={16} fontWeight={600} color="oxfordBlue">
            {t("involved.soeid_not_found", { soeid })}
          </Typography>
          <Typography fontSize={14} color="secondary" textAlign="center">
            {t("involved.soeid_not_found_hint")}
          </Typography>
          {loadedAt && (
            <Typography fontSize={12} color="secondary">
              {t("involved.soeid_loaded_at", { date: formatDate(loadedAt) })}
            </Typography>
          )}
          <InvestigationButton
            label={t("involved.soeid_retry")}
            icon="outline_refresh"
            size="medium"
            width="auto"
            onClick={search}
          />
        </Stack>
      )}

      {soeid && status === "error" && (
        <Stack alignItems="center" spacing={1.5} paddingY={3}>
          <Icon
            name="bold_danger"
            size={36}
            color={theme.palette.orange.main}
          />
          <Typography fontSize={16} color="oxfordBlue" textAlign="center">
            {t("involved.soeid_error")}
          </Typography>
          <InvestigationButton
            label={t("involved.soeid_retry")}
            icon="outline_refresh"
            size="medium"
            width="auto"
            onClick={search}
          />
        </Stack>
      )}

      {soeid && status === "done" && employee && (
        <>
          {/* Varios registros para el mismo SOEID: hay que elegir cual aplica */}
          {records.length > 1 && (
            <Stack spacing={1} marginBottom={2}>
              <Typography fontSize={14} color="oxfordBlue">
                {t("involved.soeid_multiple_records", { count: records.length })}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {records.map((record, index) => (
                  <Box
                    key={`${record.geid ?? record.employee_number}-${index}`}
                    component="button"
                    type="button"
                    aria-pressed={index === selected}
                    onClick={() => setSelected(index)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      textAlign: "left",
                      background:
                        index === selected
                          ? theme.palette.blueGray50.main
                          : "transparent",
                      border: `1.5px solid ${
                        index === selected
                          ? theme.palette.ethicsBlue.main
                          : theme.palette.blueGray200.main
                      }`,
                    }}
                  >
                    <Typography fontSize={13} fontWeight={600} color="oxfordBlue">
                      {[record.company, record.employee_number]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                    <Typography fontSize={12} color="secondary">
                      {isEmployeeActive(record)
                        ? t("involved.soeid_status_active")
                        : t("involved.soeid_status_inactive")}
                      {record.hire_date ? ` · ${formatDate(record.hire_date)}` : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}

          <Grid2 container spacing={1.5} alignItems="flex-start">
            {/* Quien es */}
            <Grid2 size={12}>
              <InvolvedSectionTitle
                icon="outline_user_tick"
                text={t("involved.soeid_found_record")}
              />
            </Grid2>
            <InvestigationBlockInfo
              size={12}
              label={t("involved.soeid_employee_name")}
              text={employee.full_name}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid")}
              text={employee.soeid}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_geid")}
              text={employee.geid ?? ""}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_employee_number")}
              text={employee.employee_number}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_employee_status")}
              text={statusLabel}
            />

            {/* Adscripcion. Llegan como codigo: sin catalogo no hay texto */}
            <Grid2 size={12} marginTop={1}>
              <InvolvedSectionTitle
                icon="outline_briefcase"
                text={t("involved.soeid_section_assignment")}
              />
            </Grid2>
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_position")}
              text={employee.position_code ?? ""}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_office")}
              text={employee.office_code ?? ""}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_section")}
              text={employee.section_code ?? ""}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_company")}
              text={employee.company ?? ""}
            />
            {/* El backend resuelve el GEID del jefe contra la nomina; si no lo
                encuentra (jefes de otra geografia) queda el GEID a secas. */}
            <InvestigationBlockInfo
              size={half}
              label={
                employee.manager
                  ? t("involved.soeid_manager")
                  : t("involved.soeid_manager_geid")
              }
              text={
                employee.manager
                  ? [employee.manager.soeid, employee.manager.full_name]
                      .filter(Boolean)
                      .join(" · ")
                  : employee.manager_geid ?? ""
              }
            />
            {employee.mocode && (
              <InvestigationBlockInfo
                size={half}
                label={t("involved.soeid_mocode")}
                text={employee.mocode}
              />
            )}
            {employee.rits_id && (
              <InvestigationBlockInfo
                size={half}
                label={t("involved.soeid_rits_id")}
                text={employee.rits_id}
              />
            )}

            {/* Trayectoria */}
            <Grid2 size={12} marginTop={1}>
              <InvolvedSectionTitle
                icon="calendar"
                text={t("involved.soeid_section_tenure")}
              />
            </Grid2>
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_hire_date")}
              text={formatDate(employee.hire_date)}
            />
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_seniority")}
              text={seniorityLabel}
            />
            {employee.group_hire_date &&
              employee.group_hire_date !== employee.hire_date && (
                <InvestigationBlockInfo
                  size={half}
                  label={t("involved.soeid_group_hire_date")}
                  text={formatDate(employee.group_hire_date)}
                />
              )}
            {employee.resignation_date && (
              <InvestigationBlockInfo
                size={half}
                label={t("involved.soeid_resignation_date")}
                text={formatDate(employee.resignation_date)}
              />
            )}
            <InvestigationBlockInfo
              size={half}
              label={t("involved.soeid_movement_date")}
              text={formatDate(employee.movement_date)}
            />

            {/* Solo aplica a quien atiende clientes; si no, viene en ceros */}
            {hasExecutiveData && (
              <>
                <Grid2 size={12} marginTop={1}>
                  <InvolvedSectionTitle
                    icon="outline_profile_2_user"
                    text={t("involved.soeid_section_executive")}
                  />
                </Grid2>
                <InvestigationBlockInfo
                  size={half}
                  label={t("involved.soeid_executive_status")}
                  text={employee.executive_status ?? ""}
                />
                <InvestigationBlockInfo
                  size={half}
                  label={t("involved.soeid_executive_office")}
                  text={employee.executive_office_code ?? ""}
                />
                <InvestigationBlockInfo
                  size={half}
                  label={t("involved.soeid_executive_start_date")}
                  text={formatDate(employee.executive_start_date)}
                />
                <InvestigationBlockInfo
                  size={half}
                  label={t("involved.soeid_executive_updated_at")}
                  text={formatDate(employee.executive_updated_at)}
                />
              </>
            )}
          </Grid2>

          {/* El registro crudo se deja a mano para cotejar contra el origen */}
          <Box
            component="button"
            type="button"
            aria-expanded={showRaw}
            onClick={() => setShowRaw((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              marginTop: 2,
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              color: theme.palette.blueGray.main,
              "&:hover": { color: theme.palette.ethicsBlue.main },
              "& svg, & svg *": { fill: "currentColor" },
            }}
          >
            <Typography fontSize={13} fontWeight={600} color="inherit">
              {t("involved.soeid_raw_record")}
            </Typography>
            <Icon
              name="arrow_down_2"
              size={10}
              style={{
                transition: "transform 150ms ease-in-out",
                transform: showRaw ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
          <Collapse in={showRaw} unmountOnExit>
            <Box
              marginTop={1}
              padding={1.5}
              borderRadius="6px"
              bgcolor={theme.palette.blueGray50.main}
              sx={{
                fontFamily: "monospace",
                fontSize: 12,
                color: theme.palette.blueGray700.main,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(employee.raw, null, 2)}
            </Box>
          </Collapse>

          {loadedAt && (
            <Typography fontSize={12} color="secondary" marginTop={1}>
              {t("involved.soeid_loaded_at", { date: formatDate(loadedAt) })}
            </Typography>
          )}

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="flex-end"
            spacing={1}
            marginTop={3}
          >
            <InvestigationButton
              label={t("involved.soeid_reject")}
              icon="close_icon"
              size="medium"
              width="auto"
              onClick={onHide}
              disabled={isLoading}
            />
            <InvestigationButton
              label={t("involved.soeid_confirm")}
              variant="success"
              icon="mark_check_bold"
              size="medium"
              width="auto"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={isLoading}
            />
          </Stack>
        </>
      )}
    </ModalContainer>
  );
};
